import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { BrandAuthHero } from "@apps/dashboard/components/brand/BrandAuthHero";
import { AppleSignInButton } from "@apps/dashboard/components/auth/AppleSignInButton";
import { AuthCenterLayout } from "@apps/dashboard/components/auth/AuthCenterLayout";
import { GoogleSignInButton } from "@apps/dashboard/components/auth/GoogleSignInButton";
import { PasskeyAuthButton } from "@apps/dashboard/components/auth/PasskeyAuthButton";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import {
  buildOAuthCallbackUrl,
  isAppleAuthEnabled,
  isGoogleAuthEnabled,
  isPasskeyAuthEnabled,
} from "@apps/dashboard/lib/env";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { tryAcceptStoredHouseInvite } from "@apps/dashboard/features/houses/accept-stored-house-invite";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { registerPasskeyWithPreAuthContext } from "@apps/dashboard/lib/auth-passkey";
import {
  fetchInvitationPreview,
  homeExpenseQueryKeys,
} from "@apps/dashboard/lib/home-expense-api";
import {
  getStoredHouseInviteToken,
  storeHouseInviteTokenFromSearchParams,
} from "@apps/dashboard/lib/house-invite-token";
import {
  shouldShowSocialProvider,
  useOAuthServerHints,
} from "@apps/dashboard/hooks/useOAuthServerHints";

const MIN_PASSWORD = 8;

export default function RegisterPage(): JSX.Element {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [appleSubmitting, setAppleSubmitting] = useState(false);
  const [passkeySubmitting, setPasskeySubmitting] = useState(false);
  const [passkeyOptInAfterSignUp, setPasskeyOptInAfterSignUp] = useState(true);
  const oauthHints = useOAuthServerHints();
  const showGoogle = shouldShowSocialProvider(
    isGoogleAuthEnabled(),
    oauthHints.status,
    oauthHints.data,
    "google",
  );
  const showApple = shouldShowSocialProvider(
    isAppleAuthEnabled(),
    oauthHints.status,
    oauthHints.data,
    "apple",
  );
  const showPasskey = isPasskeyAuthEnabled();

  useEffect(() => {
    storeHouseInviteTokenFromSearchParams(searchParams);
  }, [searchParams]);

  const inviteToken = useMemo(() => {
    const raw = searchParams.get("invite")?.trim() ?? "";
    if (raw.length >= 32) {
      return raw;
    }
    return getStoredHouseInviteToken();
  }, [searchParams]);

  const previewQuery = useQuery({
    queryKey: inviteToken
      ? homeExpenseQueryKeys.invitationPreview(locale, inviteToken)
      : ["invite-preview", "disabled"],
    queryFn: () => {
      if (!inviteToken) throw new Error("no token");
      return fetchInvitationPreview(locale, inviteToken);
    },
    enabled: Boolean(inviteToken && inviteToken.length >= 32),
    retry: false,
  });

  useEffect(() => {
    const data = previewQuery.data;
    if (data?.invitedEmail) {
      setEmail(data.invitedEmail);
    }
  }, [previewQuery.data]);

  const emailLocked = Boolean(previewQuery.data?.invitedEmail);
  const inviteHouseName = previewQuery.data?.houseName;

  const loginHref =
    inviteToken && inviteToken.length >= 32
      ? `${dashboardPaths.login}?invite=${encodeURIComponent(inviteToken)}`
      : dashboardPaths.login;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) {
      setError(t("dashboardUi.auth.errors.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("validation.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { error: err } = await authClient.signUp.email({
        name: name.trim(),
        email: email.trim(),
        password,
      });
      if (err) {
        setError(translateAuthError(err, t));
        return;
      }
      if (passkeyOptInAfterSignUp) {
        try {
          await registerPasskeyWithPreAuthContext(locale);
        } catch {
          // La cuenta ya fue creada; no bloqueamos el flujo si falla el alta de passkey.
        }
      }
      const outcome = await tryAcceptStoredHouseInvite(locale, navigate);
      if (outcome !== "navigated") {
        navigate("/", { replace: true });
      }
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogleSignIn(): Promise<void> {
    setError(null);
    setGoogleSubmitting(true);
    try {
      const { error: err } = await authClient.signIn.social({
        provider: "google",
        callbackURL: buildOAuthCallbackUrl("/"),
      });
      if (err) {
        setError(translateAuthError(err, t));
        setGoogleSubmitting(false);
      }
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
      setGoogleSubmitting(false);
    }
  }

  async function onAppleSignIn(): Promise<void> {
    setError(null);
    setAppleSubmitting(true);
    try {
      const { error: err } = await authClient.signIn.social({
        provider: "apple",
        callbackURL: buildOAuthCallbackUrl("/"),
      });
      if (err) {
        setError(translateAuthError(err, t));
        setAppleSubmitting(false);
      }
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
      setAppleSubmitting(false);
    }
  }

  async function onPasskeySignUp(): Promise<void> {
    setError(null);
    setPasskeySubmitting(true);
    try {
      const result = await authClient.signIn.passkey({ autoFill: true });
      if (result.error) {
        setError(translateAuthError(result.error, t));
        return;
      }
      const outcome = await tryAcceptStoredHouseInvite(locale, navigate);
      if (outcome !== "navigated") {
        navigate("/", { replace: true });
      }
    } catch (unknownErr) {
      setError(translateAuthError(unknownErr, t));
    } finally {
      setPasskeySubmitting(false);
    }
  }

  return (
    <AuthCenterLayout>
      <div className="relative z-10 w-full max-w-[480px]">
        <div className="mb-10 text-center">
          <BrandAuthHero
            className="mb-6"
            showTitle={false}
            showSlogan={!inviteHouseName}
          />
          <h1 className="font-headline text-3xl font-extrabold text-primary">
            {inviteHouseName
              ? t("dashboardUi.members.registerInviteHeadline")
              : t("dashboardUi.auth.registerTitle")}
          </h1>
          <p className="mt-2 text-body-md text-on-surface-variant">
            {inviteHouseName
              ? t("dashboardUi.members.registerInviteSubline", { houseName: inviteHouseName })
              : t("dashboardUi.auth.registerSubtitle")}
          </p>
        </div>

        <div className="rounded-xl border border-outline-variant/15 bg-surface-container-lowest p-10 shadow-sm">
          {error ? (
            <div
              className="mb-6 rounded-lg bg-error/10 px-4 py-3 text-body-md text-error outline outline-1 outline-error/20"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          {showPasskey || showApple || showGoogle ? (
            <div className="mb-6 space-y-4">
              {showPasskey ? (
                <div className="space-y-2">
                  <PasskeyAuthButton
                    label={t("dashboardUi.auth.signUpWithPasskey")}
                    loadingLabel={t("dashboardUi.auth.continuingWithPasskey")}
                    loading={passkeySubmitting}
                    disabled={submitting || previewQuery.isLoading || googleSubmitting || appleSubmitting}
                    onClick={onPasskeySignUp}
                  />
                  <p className="text-center text-[11px] leading-snug text-on-surface-variant/90">
                    {t("dashboardUi.auth.passkeyPreferredHint")}
                  </p>
                </div>
              ) : null}
              {showApple ? (
                <AppleSignInButton
                  label={t("dashboardUi.auth.continueWithApple")}
                  loadingLabel={t("dashboardUi.auth.continuingWithApple")}
                  loading={appleSubmitting}
                  disabled={submitting || previewQuery.isLoading || googleSubmitting || passkeySubmitting}
                  onClick={onAppleSignIn}
                />
              ) : null}
              {showGoogle ? (
                <GoogleSignInButton
                  label={t("dashboardUi.auth.continueWithGoogle")}
                  loadingLabel={t("dashboardUi.auth.continuingWithGoogle")}
                  loading={googleSubmitting}
                  disabled={submitting || previewQuery.isLoading || appleSubmitting || passkeySubmitting}
                  onClick={onGoogleSignIn}
                />
              ) : null}
              <div className="relative flex items-center justify-center">
                <span className="absolute w-full border-t border-outline-variant/20" aria-hidden />
                <span className="relative bg-surface-container-lowest px-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                  {t("dashboardUi.auth.dividerOr")}
                </span>
              </div>
            </div>
          ) : null}

          {emailLocked ? (
            <p className="mb-6 rounded-lg bg-primary/5 px-4 py-3 text-body-md text-on-surface outline outline-1 outline-primary/15">
              {t("dashboardUi.members.emailLockedForInvite")}
            </p>
          ) : null}

          <form className="space-y-5" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <label
                htmlFor="reg-name"
                className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
              >
                {t("dashboardUi.auth.name")}
              </label>
              <input
                id="reg-name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(ev) => setName(ev.target.value)}
                placeholder={t("dashboardUi.auth.namePlaceholder")}
                className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="reg-email"
                className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
              >
                {t("dashboardUi.auth.emailLabel")}
              </label>
              <input
                id="reg-email"
                name="email"
                type="email"
                autoComplete="username webauthn"
                required
                readOnly={emailLocked}
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder={t("dashboardUi.auth.emailPlaceholder")}
                className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary read-only:opacity-80"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="reg-password"
                className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
              >
                {t("dashboardUi.auth.passwordLabel")}
              </label>
              <input
                id="reg-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                placeholder={t("dashboardUi.auth.passwordPlaceholder")}
                className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <label
                htmlFor="reg-confirm"
                className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
              >
                {t("dashboardUi.auth.confirmPasswordLabel")}
              </label>
              <input
                id="reg-confirm"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(ev) => setConfirm(ev.target.value)}
                placeholder={t("dashboardUi.auth.passwordPlaceholder")}
                className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || previewQuery.isLoading}
              className="primary-gradient mt-2 h-12 w-full rounded-lg font-headline font-semibold text-on-primary shadow-md transition-all active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? t("dashboardUi.auth.signingUp") : t("dashboardUi.auth.signUpCta")}
            </button>
            <label className="mt-1 flex items-center gap-2 text-body-sm text-on-surface-variant">
              <input
                type="checkbox"
                checked={passkeyOptInAfterSignUp}
                onChange={(ev) => setPasskeyOptInAfterSignUp(ev.target.checked)}
              />
              {t("dashboardUi.auth.registerWithPasskeyAfterSignUp")}
            </label>
          </form>

          <p className="mt-8 text-center text-body-md text-on-surface-variant">
            {t("dashboardUi.auth.alreadyHaveAccount")}{" "}
            <Link to={loginHref} className="font-semibold text-primary hover:underline">
              {t("auth.login")}
            </Link>
          </p>
        </div>
      </div>
    </AuthCenterLayout>
  );
}
